import { QueryConstraint, where } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../../components/custom-table/custom-table";
import { LoaderMessage } from "../../../components/loaderMessage/loaderMessage";
import { useTablesContext } from "../../../context/tablesContextProvider";
import {
  fetchPatientCountsFromFirebase,
  getPatientSearchResultFromFirebase,
  getPatientsForHealthFacilityUsingBatching,
} from "../../../redux/slice/patients-slice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { logDataToConsole } from "../../../utils/customLogger";
import { logPatientClickEvent } from "../../../utils/events";
import { generatePatientData, PatientTableColumns } from "./patientTableData";

const PatientTable = () => {
  const navigate = useNavigate();
  const [patientData, setPatientData] = useState<any[]>([]);
  const [initialSearch, setInitialSearch] = React.useState<boolean>(true);
  const [searchText, setSearchText] = React.useState("");
  const [batchNumberRetreived, setBatchNumberRetreived] = React.useState<
    number[]
  >([]);
  const dispatch = useDispatch<AppDispatch>();
  const {
    data: patientsData,
    loading,
    count: patientsCount,
  } = useSelector((state: RootState) => state.patients);

  const { data: providerInfo } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const navigateToSinglePatient = (rowData: any) => {
    logDataToConsole(
      `Navigating to single patient with docId: ${rowData["docId"]}`,
    );
    const docId = rowData["docId"];
    navigate(`/patients/singlePatient/${docId}`);
  };

  const initialLimit = 60;

  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();
  const tableAttributes = getTableDataFromContext("allPatientsTable");

  const generateSearchConstraints = (firstName: string): QueryConstraint[] => {
    return [where("Demographics.FirstName", "==", firstName.toUpperCase())];
  };

  function search() {
    const constraints = generateSearchConstraints(searchText);
    dispatch(
      getPatientSearchResultFromFirebase(
        providerInfo?.FacilityId || "",
        ...constraints,
      ),
    );
    dispatch(
      fetchPatientCountsFromFirebase(
        providerInfo?.FacilityId || "",
        ...constraints,
      ),
    );
  }

  function fetchAllPatients() {
    const dataFromContext = tableAttributes.lastBatchFetched
      ? tableAttributes.lastBatchFetched
      : 0;

    const limitToFetch =
      dataFromContext === 0
        ? initialLimit
        : (dataFromContext + 1) * initialLimit;

    dispatch(
      fetchPatientCountsFromFirebase(providerInfo?.FacilityId || "", ...[]),
    );
    dispatch(
      getPatientsForHealthFacilityUsingBatching(
        providerInfo?.FacilityId || "",
        limitToFetch,
        ...[],
      ),
    );
  }

  const handleEndIndexOfTable = (endIndex: number) => {
    if (
      patientsData &&
      patientsCount !== null &&
      endIndex < patientsCount - 1 &&
      endIndex === patientsData.length - 1
    ) {
      const nextBatchFetch = Math.round(patientsData.length / initialLimit);
      if (nextBatchFetch > 0) {
        setBatchNumberRetreived((prevBatchNumberRetrieved) => {
          if (!prevBatchNumberRetrieved.includes(nextBatchFetch)) {
            return [...prevBatchNumberRetrieved, nextBatchFetch];
          }
          return prevBatchNumberRetrieved;
        });
      }
    }
  };

  const handleSearch = (searchText: string) => {
    setSearchText(searchText);
    setInitialSearch(false);
  };

  function fetchNextBatchPatients(facilityId: string) {
    if (batchNumberRetreived.length > 0) {
      const lastBatchNumber =
        batchNumberRetreived[batchNumberRetreived.length - 1];

      setTableDataForContext("allPatientsTable", {
        lastBatchFetched: lastBatchNumber,
      });
      dispatch(
        getPatientsForHealthFacilityUsingBatching(
          facilityId,
          initialLimit + lastBatchNumber * initialLimit,
          ...[],
        ),
      );
    }
  }

  useEffect(() => {
    fetchNextBatchPatients(providerInfo?.FacilityId || "");
  }, [JSON.stringify(batchNumberRetreived)]);

  useEffect(() => {
    if (!initialSearch && providerInfo) {
      if (searchText && searchText.length > 0) {
        search();
      } else {
        fetchAllPatients();
      }
    }
  }, [searchText, dispatch, initialSearch, providerInfo]);

  useEffect(() => {
    if (patientsData) {
      setPatientData(generatePatientData(patientsData));
    }
  }, [patientsData]);

  return (
    <>
      <div className="patients-table--container">
        {loading ? (
          <LoaderMessage message={"Fetching Patient"} />
        ) : (
          <CustomTable
            tableHeaders={PatientTableColumns}
            tableData={patientData}
            itemsPerPage={12}
            pagesPerView={5}
            count={patientsCount}
            endIndexOfTable={handleEndIndexOfTable}
            searchingText={handleSearch}
            onRowClick={(rowData) => {
              logPatientClickEvent(rowData["docId"] || "N/A");
              navigateToSinglePatient(rowData);
            }}
            tableName="allPatientsTable"
          />
        )}
      </div>
    </>
  );
};

export default PatientTable;
